import React, { useEffect, useState, useRef } from 'react';
import Select from 'react-select';

import { hasRoles, EDIT_CLIENT_ROLES, EDIT_PARTICIPANT_ROLES } from '../../../../util/RoleUtil';

import {
  deleteParticipant,
  updateParticipantLocation,
  viewParticipant,
  updateParticipantStatus,
} from '../../../../services/ParticipantService';
import { retrieveSitesForParticipantStudy } from '../../../../services/FacilitiesService';

import Modal from '../../../common/modal/Modal';
import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { Checkbox } from '../../../common/checkbox/Checkbox';

import './ParticipantEditTab.css';

export const ParticipantClientDetails = ({
  participant,
  setParticipant,
  context,
  navigateTo,
  navigateToWith,
  setSpinnerText,
  showToaster,
}) => {
  const [participantName, setParticipantName] = useState('');
  const [registrationDate, setRegistrationDate] = useState(null);
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [gender, setGender] = useState('');
  const [researchStudyConsent, setResearchStudyConsent] = useState(null);
  const [clinicalTrialConsent, setClinicalTrialConsent] = useState(null);
  const [totalEnrolments, setTotalEnrolments] = useState('');
  const [activeEnrolments, setActiveEnrolments] = useState('');
  const [enrolledStudiesList, setEnrolledStudiesList] = useState([]);
  const [lastEnrolmentDate, setLastEnrolmentDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sites, setSites] = useState([]);
  const [newSite, setNewSite] = useState(null);
  const [status, setStatus] = useState('In Screening');
  const [statusReason, setStatusReason] = useState('');
  const [changeId, setChangeId] = useState(false);
  const [newId, setNewId] = useState('');
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const [showSaveButton, setShowSaveButton] = useState(false);

  const statusOptions = useRef(['In-Screening', 'Screen Failure', 'Enrolled', 'Terminated']).current;

  const fieldsDisabled = !hasRoles(context, EDIT_PARTICIPANT_ROLES);

  useEffect(() => {
    if (participant || participant.clientDetails) {
      setParticipantName(participant?.clientDetails?.participant);
      setRegistrationDate(participant?.clientDetails?.registrationDate || null);
      if (participant?.clientDetails?.researchStudyConsent) {
        setResearchStudyConsent('Yes');
      } else {
        setResearchStudyConsent('No');
      }

      if (participant?.clientDetails?.clinicalTrialConsent) {
        setClinicalTrialConsent('Yes');
      } else {
        setClinicalTrialConsent('No');
      }
      setDateOfBirth(participant?.clientDetails?.dateOfBirth || null);
      setGender(participant?.clientDetails?.gender || null);
      setTotalEnrolments(participant?.clientDetails?.totalStudyOrTrialEnrollments || null);
      setActiveEnrolments(participant?.clientDetails?.activeStudyOrTrialEnrollments || null);
      setEnrolledStudiesList(participant?.clientDetails?.lastOrCurrentStudiesEnrolledInto || []);
      setLastEnrolmentDate(participant?.clientDetails?.lastStudyOrTrialEnrollmentDate || null);
      setStatus(participant?.clientDetails?.participantStatus || 'In Screening');
      setStatusReason(participant?.clientDetails?.participantStatusReason || '');

      setShowSaveButton(false);
    }
  }, [participant, context.username, setSpinnerText, showToaster]);

  const updateScreenWidth = () => {
    setScreenWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', updateScreenWidth);
    return () => window.removeEventListener('resize', updateScreenWidth);
  });

  const retrieveParticipant = async (username, externalReference) => {
    const [data, error] = await viewParticipant(username, externalReference);
    if (data) {
      if (data.responseStatus === 'Success') {
        setParticipant(data.participant);
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Participant Information Retrieved',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const handleChangeSiteClick = async () => {
    setIsModalOpen(true);
    setSpinnerText('Retrieving Participant Study Sites');
    const [data, error] = await retrieveSitesForParticipantStudy(
      context.username,
      participant.externalReference,
      participant.activeStudyExternalReference
    );
    if (data) {
      if (data.responseStatus === 'Success') {
        const formattedSites = data.facilityForStudyAndParticipantVOS.map((site) => ({
          value: site,
          label: `${site.facility}`,
        }));
        setSites(formattedSites);
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Sites successfully retrieved.',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveSiteChange = async () => {
    const updateParticipantLocationRequest = {
      locationExternalReference: newSite?.value?.facilityExternalReference,
      participantExternalReference: participant.externalReference,
    };

    setSpinnerText('Updating Participant Site');
    const [data, error] = await updateParticipantLocation(context.username, updateParticipantLocationRequest);
    if (data) {
      if (data.responseStatus === 'Success') {
        closeModal();
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Site successfully updated.',
        });
        retrieveParticipant(context.username, participant.externalReference);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const handleRemoveParticipantClick = async () => {
    setSpinnerText('Removing Participant');
    const [data, error] = await deleteParticipant(context.username, participant.externalReference);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Participant successfully removed.',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
    navigateTo('participants');
  };

  const handleEditDetailsClick = () => {
    navigateToWith('clientsView', 'clientsCreateGeneral', participant?.clientDetails?.clientExternalReference);
  };

  const handleStatusChange = (option) => {
    if (option.value !== 'Enrolled') {
      setChangeId(false);
      setNewId('');
    }

    setStatus(option.value);
    setStatusReason('');
    setShowSaveButton(true);
  };

  const handleSaveStatusClick = async () => {
    const updateParticipantStatusRequest = {
      participantExternalReference: participant.externalReference,
      reason: statusReason,
      newParticipantId: newId,
      status,
    };

    setSpinnerText('Updating Participant Status');
    const [data, error] = await updateParticipantStatus(context.username, updateParticipantStatusRequest);
    if (data) {
      if (data.responseStatus === 'Success') {
        closeModal();
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Status successfully updated.',
        });
        retrieveParticipant(context.username, participant.externalReference);
        setShowSaveButton(false);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const renderStatus = () => (
    <div className="participant-client-details-status-container">
      <div className="input-container">
        <label className="input-label">Participant Status: </label>
        <Select
          value={{ label: status, value: status }}
          onChange={handleStatusChange}
          options={statusOptions.map((option) => ({ label: option, value: option }))}
          classNamePrefix="input-select"
          isDisabled={fieldsDisabled}
          closeMenuOnSelect
        />
      </div>

      {status === 'Screen Failure' && (
        <>
          <div className="input-container">
            <label className="input-label">Reason for Status:</label>
            <input
              className="text-input"
              value={statusReason}
              onChange={({ target }) => {
                setStatusReason(target.value);
                setShowSaveButton(true);
              }}
              disabled={fieldsDisabled}
            />
          </div>

          {showSaveButton && (
            <SaveButton
              textColor="white"
              border="1px solid #077187"
              title="Save"
              onClick={handleSaveStatusClick}
              color="#077187"
              disabled={fieldsDisabled}
            />
          )}
        </>
      )}

      {status === 'Enrolled' && (
        <>
          <div className="input-container">
            <div className="participant-status-checkbox-container">
              <label className="input-label">Change ID?:</label>
              <div className="participant-status-checkbox">
                <Checkbox
                  checked={!changeId}
                  onChange={() => setChangeId(false)}
                  color="#6F8695"
                  disabled={fieldsDisabled}
                  fillStyle
                />
                <label className="input-label">No</label>
              </div>
              <div className="participant-status-checkbox">
                <Checkbox
                  checked={changeId}
                  onChange={() => setChangeId(true)}
                  color="#6F8695"
                  disabled={fieldsDisabled}
                  fillStyle
                />
                <label className="input-label">Yes</label>
              </div>
            </div>
          </div>

          {changeId && (
            <div className="input-container">
              <label className="input-label">New Particpant ID:</label>
              <input
                className="text-input"
                value={newId}
                onChange={({ target }) => setNewId(target.value)}
                disabled={fieldsDisabled}
              />
            </div>
          )}

          {showSaveButton && (
            <SaveButton
              textColor="white"
              border="1px solid #077187"
              title="Save"
              onClick={handleSaveStatusClick}
              color="#077187"
              disabled={fieldsDisabled}
            />
          )}
        </>
      )}

      {status === 'Terminated' && (
        <>
          <div className="input-container">
            <label className="input-label">Reason for Status:</label>
            <textarea
              className="input-area-sm"
              value={statusReason}
              onChange={({ target }) => {
                setStatusReason(target.value);
                setShowSaveButton(true);
              }}
              disabled={fieldsDisabled}
            />
          </div>

          {showSaveButton && (
            <SaveButton
              textColor="white"
              border="1px solid #077187"
              title="Save"
              onClick={handleSaveStatusClick}
              color="#077187"
              disabled={fieldsDisabled}
            />
          )}
        </>
      )}
    </div>
  );

  const renderLeftInformation = () => {
    return (
      <div className="form-section-left">
        <div className="input-container">
          <h3 className="participant-header-label">Participant: </h3>
          <h3>{participantName}</h3>
        </div>

        <div className="input-container">
          <label className="input-label">Client Registration Date: </label>
          <label>{registrationDate}</label>
        </div>

        <div className="input-container">
          <label className="input-label">Date of Birth: </label>
          <label>{dateOfBirth}</label>
        </div>

        <div className="input-container">
          <label className="input-label">Gender: </label>
          <label>{gender}</label>
        </div>

        {screenWidth > 1300 && renderStatus()}
      </div>
    );
  };

  const renderRightInformation = () => {
    return (
      <div className="form-section-right">
        <div className="input-container">
          <label className="input-label-long">Research Study Consent: </label>
          <label>{researchStudyConsent}</label>
        </div>

        <div className="input-container">
          <label className="input-label-long">Clinical Trial Consent: </label>
          <label>{clinicalTrialConsent}</label>
        </div>

        <div className="input-container">
          <label className="input-label-long">Total Study / Trial Enrolments: </label>
          <label>{totalEnrolments}</label>
        </div>

        <div className="input-container">
          <label className="input-label-long">Active Study / Trial Enrolments: </label>
          <label>{activeEnrolments}</label>
        </div>

        <div className="participant-client-details-study-list-container">
          <div className="input-container">
            <label className="input-label-long">Last / Current Study(ies) Enrolled Into: </label>
            <div className="participant-client-details-study-list">
              {enrolledStudiesList.map((study) => (
                <label>{study}</label>
              ))}
            </div>
          </div>
        </div>

        <div className="input-container">
          <label className="input-label-long">Last Study / Trial Enrolment Date: </label>
          <label>{lastEnrolmentDate}</label>
        </div>

        {screenWidth <= 1300 && renderStatus()}
      </div>
    );
  };

  return (
    <div className="tab-container participant-client-details">
      {isModalOpen && (
        <Modal
          closeModal={closeModal}
          setIsModalOpen={setIsModalOpen}
          hideButton={true}
          headerTitle={'Change Participant Study Site'}
          width={'500px'}
          height={'170px'}
          justifyContent={'center'}
          contentPadding={'2px'}
        >
          <div className="participant-client-details-change-study-modal">
            <div className="input-container" style={{ marginTop: '10px' }}>
              <label className={'input-label'}>Link Visit Code To:</label>
              <Select
                options={sites}
                closeMenuOnSelect={true}
                onChange={setNewSite}
                value={newSite || null}
                classNamePrefix="input-select"
              />
            </div>
            <div className={'review-button-container'}>
              <SaveButton
                textColor={'#6f8594'}
                border={'1px solid #6f8594'}
                title={'Cancel'}
                onClick={() => setIsModalOpen(false)}
                color={'#ffffff'}
              />
              <SaveButton
                textColor={'#ffffff'}
                border={'1px solid #6f8594'}
                title={'Save'}
                onClick={handleSaveSiteChange}
                color={'#6f8594'}
              />
            </div>
          </div>
        </Modal>
      )}
      <div className="participant-information">
        {renderLeftInformation()}
        {renderRightInformation()}
      </div>
      <div className={'participant-button-container'}>
        {hasRoles(context, EDIT_CLIENT_ROLES) && (
          <SaveButton
            textColor="#077187"
            color="#FFFFFF"
            border="1px solid #077187"
            title="Edit Details"
            onClick={handleEditDetailsClick}
          />
        )}
        {(!participant.pastVisits || participant.pastVisits.length === 0) && (
          <SaveButton
            textColor={'#077187'}
            border={'1px solid #077187'}
            title={'Remove Participant'}
            onClick={handleRemoveParticipantClick}
            disabled={fieldsDisabled}
            color={'#FFFFFF'}
          />
        )}
        <SaveButton
          textColor={'#077187'}
          border={'1px solid #077187'}
          title={'Change Site'}
          onClick={handleChangeSiteClick}
          disabled={fieldsDisabled}
          color={'#FFFFFF'}
        />
      </div>
    </div>
  );
};
